import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/hooks/useTheme';
import { CommonInput } from '@/components/ui/common-input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, Truck } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { CommonButton } from '@/components/ui/common-button';
import apiService from '@/services/apiService';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const { theme } = useTheme();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);
    try {
      await apiService.post('/api/v1/auth/forgot-password', { email });
      
      setSubmitted(true);
      toast({
        title: "Email Sent",
        description: "Check your email for password reset instructions",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to send reset instructions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="md:w-1/2 hidden md:flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-primary/5 to-background">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center opacity-5"></div>
        
        <div className="relative z-10 text-center p-8 max-w-md">
          <div className="flex justify-center mb-8">
            <img 
              src="https://ipl-fleet-tracking.s3.ap-south-1.amazonaws.com/src/logo.png" 
              alt="IPL Fleet Tracking Logo" 
              className="h-40 object-contain mx-auto"
            />
          </div>
          
          <h1 className="text-4xl font-bold text-foreground mb-4">
            IPL VLTS
          </h1>
          
          <p className="text-lg text-foreground/80 mb-2">
           VLTS management solution
          </p>
          
          <p className="text-base text-foreground/60">
            Track, manage, and optimize your VLTS operations with real-time insights and analytics.
          </p>
        </div>
      </div>
      
      <div className="md:w-1/2 w-full flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          <Card className={`border-0 bg-transparent shadow-none ${theme}`}>
            <CardHeader className="text-center space-y-4 px-0 pb-6">
              <div className="flex justify-center md:hidden mb-4">
                <img 
                  src="https://ipl-fleet-tracking.s3.ap-south-1.amazonaws.com/src/logo.png" 
                  alt="IPL VLTS Tracking Logo" 
                  className="h-16 object-contain"
                />
              </div>
              <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                <Truck className="w-8 h-8 text-primary-foreground" />
              </div>
              <div className="space-y-1">
                <CardTitle className="text-3xl font-bold text-foreground">
                  {}
                </CardTitle>
                <CardDescription className="text-base">
                  {submitted 
                    ? "Check your email for reset instructions" 
                    : "Enter your email to receive password reset instructions"}
                </CardDescription>
              </div>
            </CardHeader>
            
            <CardContent className="px-0">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Mail className="w-8 h-8 text-primary" />
                  </div>
                  <p className="text-foreground mb-2">We've sent an email to</p>
                  <p className="font-semibold text-foreground mb-4">{email}</p>
                  <p className="text-muted-foreground text-sm">
                    Click the link in the email to reset your password
                  </p>
                  <div className="mt-6">
                    <CommonButton 
                      onClick={() => navigate('/login')}
                      className="w-full h-12 text-base font-semibold rounded-lg"
                    >
                      Back to Login
                    </CommonButton>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <CommonInput
                    id="email"
                    type="email"
                    label="Email Address"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    leftIcon={<Mail className="h-5 w-5 text-muted-foreground" />}
                    disabled={loading}
                    required
                  />
                  
                  <CommonButton 
                    type="submit" 
                    className="w-full h-12 text-base font-semibold rounded-lg"
                    loading={loading}
                  >
                    Send Reset Instructions
                  </CommonButton>
                </form>
              )}
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-4 px-0 pt-6">
              <div className="text-center">
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm text-primary hover:underline"
                  disabled={loading}
                >
                  Back to Login
                </button>
              </div>
              <div className="text-center text-xs text-muted-foreground/60">
                <p>© 2025 IPL VLTS Platform</p>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;